import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle,
  Clock,
  Copy,
  MapPin,
  Package,
  Printer,
  RefreshCcw,
  Search,
  Store,
  Truck,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import {
  buildStoreSiteContext,
  extractRequestedOrderDate,
  fetchStoreSiteContext,
  formatRequestedOrderDate,
  getLocalYMD,
  getStoreLogisticsConfig,
  getStoreOrderOrigin,
  getStoreOrderOriginLabel,
  isStoreOrderClosedStatus,
  isStoreOrderDeliveredStatus,
  isStoreOrderFromPos,
  resolveOrderLogisticsDate,
  resolveStoreOrderSiteId,
  reverseStoreOrderStock,
} from '../utils/storeLogistics';
import { buildSaleItemsBySaleId } from '../utils/logisticsPlanning';
import {
  reconcileStoreOrdersClientAccounts,
  syncStoreOrderClientAccount,
} from '../utils/storeOrderAccounting';
import { getSaleItemDisplayName } from '../utils/productLabels';

const STATUS_META = {
  recibido: { label: 'Nuevo', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: AlertCircle },
  en_preparacion: { label: 'Preparando', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Package },
  listo: { label: 'Listo', color: 'bg-violet-100 text-violet-700 border-violet-200', icon: CheckCircle },
  en_camino: { label: 'En camino', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Truck },
  entregado: { label: 'Entregado', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: XCircle },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatUnits = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const getStatusMeta = (status, deliveryType) => {
  const normalized = isStoreOrderDeliveredStatus(status)
    ? 'entregado'
    : String(status || '').toLowerCase();
  const base = STATUS_META[normalized] || STATUS_META.recibido;

  if (normalized === 'listo' && deliveryType === 'retiro') {
    return { ...base, label: 'Listo p/ Retirar' };
  }

  if (normalized === 'entregado' && deliveryType === 'retiro') {
    return { ...base, label: 'Retirado' };
  }

  return base;
};

const getStatusOptions = (deliveryType) => (
  deliveryType === 'retiro'
    ? [
        { value: 'recibido', label: 'Nuevo' },
        { value: 'en_preparacion', label: 'Preparando' },
        { value: 'listo', label: 'Listo p/ Retirar' },
        { value: 'entregado', label: 'Retirado' },
        { value: 'cancelado', label: 'Cancelado' },
      ]
    : [
        { value: 'recibido', label: 'Nuevo' },
        { value: 'en_preparacion', label: 'Preparando' },
        { value: 'listo', label: 'Listo' },
        { value: 'en_camino', label: 'En camino' },
        { value: 'entregado', label: 'Entregado' },
        { value: 'cancelado', label: 'Cancelado' },
      ]
);

const cleanNotes = (value) =>
  String(value || '')
    .replace(/COMPROBANTE:\s*https?:\/\/[^\s]+/gi, '')
    .trim();

const PENDING_STORE_ORDER_STATUSES = ['recibido', 'en_preparacion', 'listo', 'en_camino'];
const COMPLETED_STORE_ORDER_STATUSES = ['entregado', 'retirado'];

export default function LogisticaEntregas() {
  const { profile, organization: authOrganization } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState(() => getLocalYMD(new Date()));
  // ✅ FIX bug #2 multi-sucursal: SiteContext es la fuente única. Antes leía
  // localStorage al montar y no se enteraba del cambio de sucursal del Topbar.
  const { selectedSiteId: contextSiteId, setSelectedSiteId: setContextSiteId } = useSite();
  const selectedSiteId = contextSiteId || '';
  const setSelectedSiteId = (next) => setContextSiteId(next || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, order: null, nextStatus: '' });
  const [sites, setSites] = useState([]);
  const [storeConfig, setStoreConfig] = useState({ theme_config: {} });
  const [organization, setOrganization] = useState(null);
  const [storeSiteContext, setStoreSiteContext] = useState(() => buildStoreSiteContext());
  const [orders, setOrders] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [dateDrafts, setDateDrafts] = useState({});
  const fetchDataRef = useRef(() => {});
  const realtimeRefreshTimeoutRef = useRef(null);

  const logisticsConfig = useMemo(() => getStoreLogisticsConfig(storeConfig), [storeConfig]);

  // ✅ FIX bug #2: persistencia centralizada en SiteContext. Ya no escribimos directo.

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadBaseData();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, showCompleted]);

  useEffect(() => {
    if (!profile?.organization_id) return undefined;

    const organizationId = profile.organization_id;
    const scheduleRefresh = () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
      }

      realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
        fetchDataRef.current?.();
      }, 250);
    };

    const channel = supabase
      .channel(`logistica_entregas_store_orders_${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'store_orders',
          filter: `organization_id=eq.${organizationId}`,
        },
        scheduleRefresh,
      )
      .subscribe();

    return () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [profile?.organization_id]);

  const loadBaseData = async () => {
    try {
      const organizationId = profile.organization_id;
      const [sitesRes, storeConfigRes, siteContextRes] = await Promise.all([
        supabase
          .from('sitios')
          .select('id, nombre')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .order('nombre'),
        supabase
          .from('store_config')
          .select('theme_config')
          .eq('organization_id', organizationId)
          .maybeSingle(),
        fetchStoreSiteContext({ supabase, organizationId }),
      ]);

      if (sitesRes.error) throw sitesRes.error;
      if (storeConfigRes.error) throw storeConfigRes.error;
      if (siteContextRes.error) throw siteContextRes.error;

      setSites(sitesRes.data || []);
      setStoreConfig(storeConfigRes.data || { theme_config: {} });
      // organization (name, full_address, phone) viene del AuthContext.
      setOrganization(authOrganization || null);
      setStoreSiteContext(siteContextRes.data || buildStoreSiteContext());
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar la base de entregas.');
    }
  };

  const fetchData = async () => {
    const firstLoad = loading;
    if (!firstLoad) setRefreshing(true);

    try {
      const statusFilter = showCompleted
        ? COMPLETED_STORE_ORDER_STATUSES
        : PENDING_STORE_ORDER_STATUSES;

      const { data, error } = await supabase
        .from('store_orders')
        .select(`
          id,
          sale_id,
          status,
          created_at,
          shipping_address,
          customer_snapshot,
          tracking_code,
          delivery_type,
          customer_phone,
          shipping_cost,
          notes,
          store_slug,
          sale:sales!store_orders_sale_id_fkey (
            id,
            total,
            site_id,
            payment_method,
            billing_source
          )
        `)
        .eq('organization_id', profile.organization_id)
        .in('status', statusFilter)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setOrders(data || []);
      setSaleItems([]);

      void reconcileStoreOrdersClientAccounts({
        orders: data || [],
        organizationId: profile.organization_id,
      }).catch((reconcileError) => {
        console.error('Error reconciliando pedidos logisticos:', reconcileError);
      });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo sincronizar la agenda de entregas.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  fetchDataRef.current = fetchData;

  const scopedOrders = useMemo(() => {
    return (orders || [])
      .filter((order) => resolveOrderLogisticsDate(order) === selectedDateKey)
      .filter((order) => {
        if (!selectedSiteId) return true;
        return resolveStoreOrderSiteId(order, storeSiteContext) === selectedSiteId;
      })
      .sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime());
  }, [orders, selectedDateKey, selectedSiteId, storeSiteContext]);

  useEffect(() => {
    let cancelled = false;

    const loadScopedSaleItems = async () => {
      const saleIds = Array.from(new Set(scopedOrders.map((order) => order.sale_id).filter(Boolean)));

      if (saleIds.length === 0) {
        setSaleItems([]);
        return;
      }

      try {
        const saleItemsRes = await supabase
          .from('sale_items')
          .select('sale_id, product_id, product_name_snapshot, quantity')
          .in('sale_id', saleIds);

        if (saleItemsRes.error) throw saleItemsRes.error;
        if (!cancelled) {
          setSaleItems(saleItemsRes.data || []);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setSaleItems([]);
          toast.error('No se pudieron cargar los items de las entregas visibles.');
        }
      }
    };

    void loadScopedSaleItems();

    return () => {
      cancelled = true;
    };
  }, [scopedOrders]);

  const saleItemsBySaleId = useMemo(() => buildSaleItemsBySaleId(saleItems), [saleItems]);
  const siteNameById = useMemo(() => new Map((sites || []).map((site) => [site.id, site.nombre])), [sites]);

  const filteredOrders = useMemo(() => {
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();

    return scopedOrders
      .filter((order) => {
        if (!normalizedSearch) return true;

        return [
          order.tracking_code,
          order.customer_snapshot?.name,
          order.customer_snapshot?.contact,
          order.customer_phone,
        ]
          .some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
      });
  }, [scopedOrders, searchTerm]);

  const stats = useMemo(() => {
    const units = filteredOrders.reduce((acc, order) => {
      const nextUnits = (saleItemsBySaleId.get(order.sale_id) || []).reduce(
        (itemsAcc, item) => itemsAcc + Number(item.quantity || 0),
        0,
      );
      return acc + nextUnits;
    }, 0);

    return {
      orders: filteredOrders.length,
      retiro: filteredOrders.filter((order) => order.delivery_type === 'retiro').length,
      envio: filteredOrders.filter((order) => order.delivery_type === 'envio').length,
      units,
    };
  }, [filteredOrders, saleItemsBySaleId]);

  const pageTitle = showCompleted ? 'Entregas realizadas' : 'Entregas pendientes';
  const pageSubtitle = showCompleted
    ? 'Historial diario de entregas completadas para reimprimir remitos y duplicados.'
    : 'Agenda diaria de retiros y envios con estados, remitos y duplicados.';
  const filterLabel = showCompleted ? 'Realizadas' : 'Pendientes';

  const handleOrderDateChange = async (order, nextDate) => {
    const normalizedDate = String(nextDate || '').trim();
    if (!normalizedDate) return;

    const nextShippingAddress = {
      ...(order.shipping_address || {}),
      requested_date: normalizedDate,
    };
    const nextCustomerSnapshot = {
      ...(order.customer_snapshot || {}),
      requested_date: normalizedDate,
    };

    setDateDrafts((prev) => ({ ...prev, [order.id]: normalizedDate }));
    setUpdatingOrderId(order.id);

    try {
      const { error } = await supabase
        .from('store_orders')
        .update({
          shipping_address: nextShippingAddress,
          customer_snapshot: nextCustomerSnapshot,
        })
        .eq('id', order.id)
        .eq('organization_id', profile.organization_id);

      if (error) throw error;

      setOrders((current) =>
        current.map((row) => (
          row.id === order.id
            ? {
                ...row,
                shipping_address: nextShippingAddress,
                customer_snapshot: nextCustomerSnapshot,
              }
            : row
        )),
      );

      toast.success('Fecha de entrega actualizada.');
    } catch (error) {
      console.error(error);
      toast.error('No se pudo actualizar la fecha de entrega.');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleStatusChange = async (order, nextStatus) => {
    if (order.status === nextStatus) return;

    setUpdatingOrderId(order.id);

    try {
      const orderOrigin = getStoreOrderOrigin(order);
      const managesCommercialFlow = !isStoreOrderFromPos(order);
      const { error: orderError } = await supabase
        .from('store_orders')
        .update({ status: nextStatus, organization_id: profile.organization_id })
        .eq('id', order.id);

      if (orderError) throw orderError;

      const saleStatus =
        isStoreOrderDeliveredStatus(nextStatus)
          ? 'completed'
          : nextStatus === 'cancelado'
            ? 'canceled'
            : 'pending';

      if (managesCommercialFlow && order.sale_id) {
        const { error: saleError } = await supabase
          .from('sales')
          .update({ status: saleStatus })
          .eq('id', order.sale_id);

        if (saleError) {
          console.error('Error sync sales:', saleError);
        }
      }

      if (managesCommercialFlow && order.sale_id) {
        const clientAccountSync = await syncStoreOrderClientAccount({
          orderId: order.id,
          saleId: order.sale_id,
          organizationId: profile.organization_id,
          trackingCode: order.tracking_code,
          nextStatus,
          shippingCost: order.shipping_cost || 0,
          orderOrigin,
        });

        if (nextStatus !== 'cancelado' && clientAccountSync?.reason === 'missing_client') {
          toast('Pedido entregado, pero no tiene cliente vinculado para cuenta corriente.', {
            icon: '!',
          });
        }
      }

      if (managesCommercialFlow && nextStatus === 'cancelado' && order.sale_id) {
        const reverseRes = await reverseStoreOrderStock({
          supabase,
          organizationId: profile.organization_id,
          order,
          siteContext: storeSiteContext,
        });

        if (reverseRes.error) {
          console.error('Error revirtiendo stock:', reverseRes.error);
          toast.error('Pedido cancelado, pero no se pudo revertir el stock.');
        }
      }

      setOrders((current) => current.map((row) => (
        row.id === order.id ? { ...row, status: nextStatus } : row
      )));
      toast.success('Estado de entrega actualizado.');
    } catch (error) {
      console.error(error);
      toast.error('No se pudo actualizar el estado de entrega.');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const requestStatusChange = (order, nextStatus) => {
    if (order.status === nextStatus) return;

    // Un pedido cancelado es TERMINAL: des-cancelarlo no re-descuenta stock y
    // re-cancelarlo lo repondría de nuevo (stock inflado). Se bloquea la salida.
    if (String(order.status || '').toLowerCase() === 'cancelado') {
      toast.error('Un pedido cancelado no puede cambiar de estado.');
      return;
    }

    if (nextStatus === 'cancelado') {
      setCancelConfirm({ open: true, order, nextStatus });
      return;
    }

    handleStatusChange(order, nextStatus);
  };

  const closeCancelConfirm = () => {
    setCancelConfirm({ open: false, order: null, nextStatus: '' });
  };

  const confirmCancelOrder = async () => {
    if (!cancelConfirm.order || !cancelConfirm.nextStatus) {
      closeCancelConfirm();
      return;
    }

    const targetOrder = cancelConfirm.order;
    const targetStatus = cancelConfirm.nextStatus;
    closeCancelConfirm();
    await handleStatusChange(targetOrder, targetStatus);
  };

  const handlePrintRemito = async (order, duplicate = false) => {
    const currentItems = saleItemsBySaleId.get(order.sale_id) || [];
    let items = currentItems;

    if (items.length === 0 && order.sale_id) {
      const saleItemsRes = await supabase
        .from('sale_items')
        .select('product_name_snapshot, quantity')
        .eq('sale_id', order.sale_id);

      if (saleItemsRes.error) {
        console.error(saleItemsRes.error);
        toast.error('No se pudieron cargar los items del remito.');
        return;
      }

      items = saleItemsRes.data || [];
    }

    const requestedDate = extractRequestedOrderDate(order);
    const requestedDateLabel = formatRequestedOrderDate(resolveOrderLogisticsDate(order));
    const siteId = resolveStoreOrderSiteId(order, storeSiteContext);
    const siteLabel = siteNameById.get(siteId) || 'Sucursal';
    const customerName = order.customer_snapshot?.name || order.customer_snapshot?.contact || 'Cliente';
    const customerPhone = order.customer_phone || order.customer_snapshot?.phone || 'Sin telefono';
    const deliveryLabel = order.delivery_type === 'envio' ? 'Envio' : 'Retiro';
    const addressLabel = order.delivery_type === 'envio'
      ? [order.shipping_address?.address, order.shipping_address?.city].filter(Boolean).join(' - ') || 'Sin direccion'
      : `Retira en ${siteLabel}`;
    const notesText = cleanNotes(order.notes);
    const duplicateLabel = duplicate ? 'DUPLICADO' : '';

    const printWindow = window.open('', '_blank', 'width=980,height=760');
    if (!printWindow) {
      toast.error('No se pudo abrir la ventana de impresion.');
      return;
    }

    const rows = items.length > 0
      ? items.map((item) => `
          <tr>
            <td>${formatUnits(item.quantity)}x</td>
            <td>${getSaleItemDisplayName(item)}</td>
          </tr>
        `).join('')
      : `
        <tr>
          <td colspan="2">Sin items cargados</td>
        </tr>
      `;

    printWindow.document.write(`
      <html>
        <head>
          <title>Remito ${order.tracking_code}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #0f172a; }
            .sheet { max-width: 860px; margin: 0 auto; position: relative; }
            .duplicate { position: absolute; top: 18px; right: 0; font-size: 12px; font-weight: 800; letter-spacing: .18em; color: #dc2626; }
            .header { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 24px; }
            .brand { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .18em; color: #64748b; }
            .title { font-size: 30px; font-weight: 800; margin: 6px 0; }
            .meta { font-size: 12px; color: #475569; margin-top: 4px; }
            .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-bottom: 20px; }
            .card { border: 1px solid #e2e8f0; border-radius: 16px; padding: 14px; }
            .label { font-size: 10px; text-transform: uppercase; letter-spacing: .16em; color: #64748b; font-weight: 800; margin-bottom: 8px; }
            .value { font-size: 15px; font-weight: 700; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border-bottom: 1px solid #e2e8f0; padding: 10px 8px; font-size: 13px; }
            th { text-transform: uppercase; letter-spacing: .12em; font-size: 10px; color: #64748b; text-align: left; }
            .footer { margin-top: 24px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
            .sign { border-top: 1px solid #cbd5e1; padding-top: 10px; text-transform: uppercase; letter-spacing: .16em; font-size: 10px; color: #64748b; }
            @media print { body { margin: 12px; } }
          </style>
        </head>
        <body>
          <div class="sheet">
            ${duplicateLabel ? `<div class="duplicate">${duplicateLabel}</div>` : ''}
            <div class="header">
              <div>
                <div class="brand">${organization?.name || BRAND_NAME}</div>
                <div class="title">Remito #${order.tracking_code}</div>
                <div class="meta">Emitido ${new Date().toLocaleString('es-AR')}</div>
                <div class="meta">Origen ${getStoreOrderOriginLabel(order)} - ${deliveryLabel}</div>
              </div>
              <div>
                <div class="meta">${organization?.full_address || ''}</div>
                <div class="meta">${organization?.phone || ''}</div>
              </div>
            </div>

            <div class="grid">
              <div class="card">
                <div class="label">Cliente</div>
                <div class="value">${customerName}</div>
                <div class="meta">${customerPhone}</div>
              </div>
              <div class="card">
                <div class="label">Entrega</div>
                <div class="value">${deliveryLabel}</div>
                <div class="meta">${addressLabel}</div>
                <div class="meta">Para ${requestedDate ? requestedDateLabel : formatRequestedOrderDate(resolveOrderLogisticsDate(order))}</div>
              </div>
            </div>

            <div class="card">
              <div class="label">Detalle</div>
              <table>
                <thead>
                  <tr>
                    <th style="width: 120px;">Cantidad</th>
                    <th>Producto</th>
                  </tr>
                </thead>
                <tbody>
                  ${rows}
                </tbody>
              </table>
            </div>

            ${notesText ? `
              <div class="card" style="margin-top: 16px;">
                <div class="label">Observaciones</div>
                <div class="value" style="font-size: 13px; font-weight: 600;">${notesText}</div>
              </div>
            ` : ''}

            <div class="footer">
              <div class="sign">Firma entrega</div>
              <div class="sign">Firma recibe</div>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">
          Cargando entregas...
        </p>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Truck size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              {pageTitle}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {pageSubtitle}
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <MapPin size={14} className="text-slate-400" />
            <select
              value={selectedSiteId}
              onChange={(event) => setSelectedSiteId(event.target.value)}
              className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer"
            >
              <option value="">Consolidado</option>
              {sites.map((site) => <option key={site.id} value={site.id}>{site.nombre}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <CalendarDays size={14} className="text-slate-400" />
            <input
              type="date"
              value={selectedDateKey}
              onChange={(event) => setSelectedDateKey(event.target.value)}
              className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer"
            />
          </div>

          <button
            type="button"
            onClick={fetchData}
            disabled={refreshing}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCcw size={14} className={refreshing ? 'animate-spin' : ''} /> Actualizar
          </button>

          <button
            type="button"
            onClick={() => setShowCompleted((current) => !current)}
            className={`px-4 py-2.5 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${
              showCompleted
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : 'border-slate-200 bg-white text-slate-600 hover:border-[#51A7FD] hover:text-[#0B2A36]'
            }`}
          >
            {showCompleted ? 'Ver pendientes' : 'Ver realizadas'}
          </button>
        </div>
      </div>

      {!logisticsConfig.request_date_enabled ? (
        <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] text-amber-800">
          El campo de fecha del pedido esta desactivado. Las entregas usan la fecha de ingreso como respaldo.
        </div>
      ) : null}

      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Entregas del dia</p>
            <Truck size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{stats.orders}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{formatUnits(stats.units)} unidades</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Retiros</p>
            <Store size={16} className="text-violet-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{stats.retiro}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pedidos para mostrador</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Envios</p>
            <Truck size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{stats.envio}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pedidos a despacho</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Agenda filtrada</p>
            <p className="text-sm font-black uppercase text-slate-800">
              {new Date(`${selectedDateKey}T12:00:00`).toLocaleDateString('es-AR', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
              })}
            </p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{filterLabel}</p>
          </div>

          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm md:min-w-[280px]">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por pedido o cliente..."
              className="w-full bg-transparent outline-none text-[11px] font-bold text-slate-700 placeholder:text-slate-300"
            />
          </div>
        </div>

        <div className="p-3 md:p-4 space-y-3 bg-slate-50/50">
          {filteredOrders.length > 0 ? filteredOrders.map((order) => {
            const status = getStatusMeta(order.status, order.delivery_type);
            const StatusIcon = status.icon;
            const orderItems = saleItemsBySaleId.get(order.sale_id) || [];
            const units = orderItems.reduce((acc, item) => acc + Number(item.quantity || 0), 0);
            const resolvedDate = resolveOrderLogisticsDate(order);
            const editableDate = dateDrafts[order.id] || extractRequestedOrderDate(order) || resolvedDate;
            const siteId = resolveStoreOrderSiteId(order, storeSiteContext);
            const siteLabel = siteNameById.get(siteId) || 'Sin sitio';
            const sourceLabel = getStoreOrderOriginLabel(order);
            const notesText = cleanNotes(order.notes);

            return (
              <div key={order.id} className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[11px] font-black uppercase tracking-wider text-slate-800">
                        #{order.tracking_code || order.id.slice(0, 8)}
                      </p>
                      <span className="px-2 py-1 rounded-lg bg-slate-100 text-[8px] font-black uppercase tracking-widest text-slate-600">
                        {sourceLabel}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest ${status.color}`}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </div>

                    <p className="mt-2 text-sm font-black text-slate-800">
                      {order.customer_snapshot?.name || order.customer_snapshot?.contact || 'Cliente'}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] font-bold text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(order.created_at).toLocaleString('es-AR')}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} />
                        {siteLabel}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        {order.delivery_type === 'envio' ? <Truck size={12} /> : <Store size={12} />}
                        {order.delivery_type === 'envio' ? 'Envio' : 'Retiro'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:min-w-[520px]">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Programado</p>
                      <p className="mt-1 text-[10px] font-black text-slate-700">{formatRequestedOrderDate(resolvedDate)}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Unidades</p>
                      <p className="mt-1 text-[10px] font-black text-slate-700">{formatUnits(units)}u</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total</p>
                      <p className="mt-1 text-[10px] font-black text-slate-700">{formatCurrency(order.sale?.total)}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Pago</p>
                      <p className="mt-1 text-[10px] font-black text-slate-700 truncate">{order.sale?.payment_method || 'Pendiente'}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_260px] gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Detalle del pedido</p>
                      <span className="rounded-full bg-[#0B2A36] px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-white">
                        {orderItems.length} item(s)
                      </span>
                    </div>

                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {orderItems.length > 0 ? orderItems.map((item, index) => (
                        <div key={`${order.id}-item-${item.product_id || index}`} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[9px] font-bold text-slate-600">
                          {formatUnits(item.quantity)} x {getSaleItemDisplayName(item)}
                        </div>
                      )) : (
                        <div className="text-[10px] font-bold text-slate-400">Sin items visibles.</div>
                      )}
                    </div>

                    {notesText ? (
                      <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 px-3 py-2 text-[10px] font-bold text-amber-800">
                        {notesText}
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                      {logisticsConfig.request_date_label || 'Dia del pedido'}
                    </p>
                    {logisticsConfig.request_date_enabled ? (
                      <input
                        type="date"
                        value={editableDate}
                        disabled={updatingOrderId === order.id}
                        onChange={(event) => handleOrderDateChange(order, event.target.value)}
                        className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD] disabled:opacity-60"
                      />
                    ) : (
                      <p className="mt-2 text-[10px] font-bold text-slate-500">
                        Se usa la fecha de ingreso del pedido hasta activar esta opcion.
                      </p>
                    )}
                    <p className="mt-3 text-[8px] font-black uppercase tracking-widest text-slate-400">Estado de entrega</p>
                    <select
                      value={order.status}
                      disabled={updatingOrderId === order.id}
                      onChange={(event) => requestStatusChange(order, event.target.value)}
                      className={`mt-2 w-full rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest outline-none appearance-none ${status.color} disabled:opacity-60`}
                    >
                      {getStatusOptions(order.delivery_type).map((option) => (
                        <option key={`${order.id}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => handlePrintRemito(order, false)}
                    className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:border-[#51A7FD] hover:text-[#0B2A36] transition-all flex items-center gap-2"
                  >
                    <Printer size={13} /> Remito
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePrintRemito(order, true)}
                    className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-all flex items-center gap-2"
                  >
                    <Copy size={13} /> Duplicado
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/sistema/pedido-web-detalle/${order.id}`)}
                    className="px-3 py-2 rounded-xl bg-[#0B2A36] text-[10px] font-black uppercase tracking-widest text-white hover:bg-slate-800 transition-all"
                  >
                    Ver pedido
                  </button>
                </div>
              </div>
            );
          }) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-16 text-center text-slate-300">
              <Truck size={28} className="mx-auto" />
              <p className="mt-2 text-[10px] font-black uppercase tracking-widest">
                {showCompleted ? 'Sin entregas realizadas para este dia' : 'Sin entregas pendientes para este dia'}
              </p>
            </div>
          )}
        </div>
      </div>

      {cancelConfirm.open ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B2A36]/35 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-[28px] border border-rose-200 bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-5 py-4 border-b border-rose-100 bg-rose-50">
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500">Confirmar cancelacion</p>
              <h3 className="mt-1 text-lg font-black text-slate-800 uppercase tracking-tight">
                Cancelar pedido
              </h3>
            </div>

            <div className="px-5 py-4">
              <p className="text-[11px] font-bold text-slate-600 leading-relaxed">
                Vas a cancelar el pedido{' '}
                <span className="text-slate-800">#{cancelConfirm.order?.tracking_code || cancelConfirm.order?.id?.slice(0, 8) || ''}</span>.
              </p>
            </div>

            <div className="px-5 pb-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCancelConfirm}
                className="px-4 py-2.5 rounded-2xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all"
              >
                No
              </button>
              <button
                type="button"
                onClick={confirmCancelOrder}
                className="px-4 py-2.5 rounded-2xl bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-rose-600 transition-all shadow-sm"
              >
                Si, cancelar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
